import { useNavigate, useSearch } from '@tanstack/react-router';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { useRunOnce } from '@/hooks/use-run-once';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackEligibilityCheckpoint } from '@/lib/analytics';
import { trackInsuranceSendLink } from '@/lib/analytics/events/insurance-send-link';
import {
  getDefaultResolveFamily,
  getResolveBackOverride,
} from '@/types/insurance-builder-eligibility-revamp';
import { getBaseInsuranceEligibilityEventAttributes } from '@/types/insurance-builder.type';
import {
  InsuranceSendLinkForm,
  type InsuranceSendLinkFormData,
} from '@/views/insurance/components/InsuranceSendLinkForm.tsx';

export const InsuranceSendLink = () => {
  const navigate = useNavigate();
  const {
    override,
    planFamily,
    planType,
    preserveLaterFamilies,
    previousPlanFamily,
    previousUploadPlanType,
  } = useSearch({ from: '/appLayout/wizardLayout/insurance/send-link' });
  const { saveSession } = useSessionSave();

  useRunOnce(function trackEligibilityCheckpointOnMount() {
    const { insuranceBuilder, insuranceZipCode } = useSessionStore.getState();
    const attributes = getBaseInsuranceEligibilityEventAttributes(
      insuranceBuilder,
      insuranceZipCode,
      { usePlanFamilyStatus: true }
    );

    trackEligibilityCheckpoint({
      ...attributes,
    });
  });

  const onSubmit = (formValues: InsuranceSendLinkFormData) => {
    useSessionStore.getState().updateState({
      contactPhoneNumber: formValues.phoneNumber,
      contactEmail: formValues.email,
    });

    trackInsuranceSendLink({
      primaryContactEmail: formValues.email,
      primaryContactPhoneNumber: formValues.phoneNumber,
    });

    saveSession();

    navigate({ to: '/insurance/link-sent', replace: true });
  };

  const handleBack = () => {
    const { insuranceBuilder } = useSessionStore.getState();

    if (planType) {
      navigate({
        to: '/insurance/upload',
        search: {
          planType,
          override,
          previousPlanFamily,
          ...(preserveLaterFamilies && { preserveLaterFamilies }),
        },
        replace: true,
      });
      return;
    }

    const fallbackFamily = getDefaultResolveFamily(insuranceBuilder);
    const backFamily = planFamily ?? fallbackFamily;

    navigate({
      to: '/insurance/resolve',
      search: {
        planFamily: backFamily,
        override: getResolveBackOverride({
          builder: insuranceBuilder,
          family: backFamily,
          override,
        }),
        previousPlanFamily,
        previousUploadPlanType,
        ...(preserveLaterFamilies && { preserveLaterFamilies }),
      },
      replace: true,
    });
  };

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Back" />
      <Card className="mx-auto gap-8">
        <CardHeader>
          <CardTitle className="sm:text-[32px]">Let&apos;s find your insurance info</CardTitle>
          <div className="mt-8 space-y-4 text-base leading-6">
            <p className="text-main">Try one of these quick ways to find it:</p>
            <CardDescription className="text-charcoal-700 mt-0 space-y-4">
              <p>
                <strong>Ask a caregiver or family member:</strong> they may have it on file
              </p>
              <p>
                <strong>Look at recent paperwork:</strong> a bill, EOB, or anything from a recent
                doctor&apos;s visit
              </p>
              <p>
                <strong>Log into your insurer&apos;s portal or app:</strong> your ID is usually on
                the home screen or member profile
              </p>
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="border-t border-border pt-8">
          <InsuranceSendLinkForm onSubmit={onSubmit} />
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
};
